import React, { createContext, useContext, useReducer, useEffect, useState, ReactNode } from 'react';
import { Product, ProductState, ProductAction, Category, Subcategory } from '../types';
import { 
  fetchProductsFromSupabase, 
  addProductToSupabase, 
  updateProductInSupabase, 
  deleteProductFromSupabase 
} from '../utils/supabaseProducts';
import { showError } from '../utils/alerts';

export const categories: Category[] = [
  {
    id: 'pinon-hadi',
    name: 'Pinon Hadi',
    image: '/categories/pinon-hadi.jpg',
    subcategories: [
      { id: 'exclusive-mizoram-pinon-hadi', name: 'Exclusive Mizoram Pinon Hadi' },
      { id: 'high-quality-hottali-pinon-hadi', name: 'High Quality Hottali Pinon Hadi' },
      { id: 'hottali-pinon-hadi-budget', name: 'Hottali Pinon Hadi (budget friendly)' },
      { id: 'textile-pinon-hadi', name: 'Textile Pinon Hadi' },
      { id: 'tanttuj-pinon-hadi', name: 'Tanttuj Pinon Hadi' },
      { id: 'single-pinon', name: 'Single Pinon' }
    ]
  },
  {
    id: 'fusion-skirt-tops',
    name: 'Fusion Skirt-Tops',
    image: '/categories/fusion-skirt-tops.jpg',
    subcategories: [
      { id: 'round-skirt', name: 'Round Skirt' },
      { id: 'pinon-design-wrapper', name: 'Pinon Design Wrapper' },
      { id: 'plain-wrapper-skirt', name: 'Plain Wrapper Skirt' },
      { id: 'design-wrapper-skirt', name: 'Design Wrapper Skirt' },
      { id: 'skirt-tops-set', name: 'Skirt -Tops Set' },
      { id: 'kurti', name: 'Kurti' }
    ]
  },
  {
    id: 'cord-set',
    name: 'Cord Set',
    image: '/categories/cord-set.jpg',
    subcategories: [
      { id: 'palazzo-pant', name: 'Palazzo pant' },
      { id: 'pant-tops-set', name: 'Pant - Tops Set' },
      { id: 'single-kurti', name: 'Single Kurti' },
      { id: 'printed-palazzo', name: 'Printed Palazzo' }
    ]
  },
  {
    id: 'blouse-collection',
    name: 'Blouse Collection',
    image: '/categories/blouse-collection.jpg',
    subcategories: []
  }
];

interface ProductContextType {
  products: Product[];
  addProduct: (productData: Partial<Product>) => Promise<void> | void;
  updateProduct: (id: string | number, productData: Partial<Product>) => Promise<void> | void;
  deleteProduct: (id: string | number) => Promise<void> | void;
  getProductById: (id: string | number) => Product | undefined;
  getProductsByCategory: (category: string) => Product[];
  searchProducts: (searchTerm: string) => Product[];
  clearAllProducts: () => void;
  exportProducts: () => void;
  importProducts: (productsData: Product[] | string) => boolean;
  getProductsCount: () => number;
  getAvailableProducts: () => Product[];
  getAllCategories: () => Category[];
  getCategoryById: (categoryId: string) => Category | undefined;
  getSubcategoryById: (categoryId: string, subcategoryId: string) => Subcategory | null;
  getProductsBySubcategory: (categoryId: string, subcategoryId: string) => Product[];
  loading?: boolean;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

const PRODUCTS_STORAGE_KEY = 'fusion_mart_products';

const saveProductsToStorage = (products: Product[]): void => {
  try {
    localStorage.setItem(PRODUCTS_STORAGE_KEY, JSON.stringify(products));
  } catch (error) {
    console.error('Error saving products to localStorage:', error);
  }
};

const loadProductsFromStorage = (): Product[] => {
  try {
    const stored = localStorage.getItem(PRODUCTS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading products from localStorage:', error);
    return [];
  }
};

const productReducer = (state: ProductState, action: ProductAction): ProductState => {
  switch (action.type) {
    case 'ADD_PRODUCT': {
      // If product already has an id (from Supabase), use it; otherwise generate one
      const newProduct: Product = {
        ...action.payload,
        id: action.payload.id || Date.now(),
        image: action.payload.image || '/logo/fusionmartlogo.png',
        images: action.payload.images || [],
        features: action.payload.features || [],
        sizes: action.payload.sizes || [],
        available: action.payload.available ?? true,
        category: action.payload.category || '',
        name: action.payload.name || ''
      } as Product;
      return {
        ...state,
        products: [...state.products, newProduct]
      };
    }
      
    case 'UPDATE_PRODUCT': {
      const updatedProducts = state.products.map(product => {
        if (String(product.id) === String(action.payload.id)) {
          return { 
            ...product, 
            ...action.payload,
            images: action.payload.images || product.images || [],
            features: action.payload.features || product.features || [],
            sizes: action.payload.sizes || product.sizes || []
          };
        }
        return product;
      });
      return {
        ...state,
        products: updatedProducts
      };
    }
      
    case 'DELETE_PRODUCT':
      return {
        ...state,
        products: state.products.filter(product => String(product.id) !== String(action.payload))
      };
      
    case 'SET_PRODUCTS':
      return {
        ...state,
        products: action.payload
      };
      
    default:
      return state;
  }
};

interface ProductProviderProps {
  children: ReactNode;
}

export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(productReducer, {
    products: []
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [useSupabase, setUseSupabase] = useState<boolean>(true);

  // Load products from Supabase on mount
  useEffect(() => {
    const loadProducts = async (): Promise<void> => {
      try {
        const products = await fetchProductsFromSupabase();
        if (products.length > 0) {
          dispatch({ type: 'SET_PRODUCTS', payload: products });
          setUseSupabase(true);
        } else {
          // Fallback to localStorage if Supabase is empty
          const localProducts = loadProductsFromStorage();
          if (localProducts.length > 0) {
            dispatch({ type: 'SET_PRODUCTS', payload: localProducts });
            setUseSupabase(false);
          }
        }
      } catch (error) {
        console.error('Error loading products from Supabase, falling back to localStorage:', error);
        const localProducts = loadProductsFromStorage();
        dispatch({ type: 'SET_PRODUCTS', payload: localProducts });
        setUseSupabase(false);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Sync to localStorage as backup (only if not using Supabase)
  useEffect(() => {
    if (!useSupabase && state.products.length > 0) {
      saveProductsToStorage(state.products);
    }
  }, [state.products, useSupabase]);

  const addProduct = async (productData: Partial<Product>): Promise<void> => {
    try {
      if (useSupabase) {
        const newProduct = await addProductToSupabase(productData);
        dispatch({
          type: 'ADD_PRODUCT',
          payload: newProduct
        });
      } else {
        // Fallback to localStorage
        dispatch({
          type: 'ADD_PRODUCT',
          payload: productData
        });
      }
    } catch (error) {
      console.error('Error adding product:', error);
      showError('Failed to add product. Please try again.');
      // Fallback to localStorage
      dispatch({
        type: 'ADD_PRODUCT',
        payload: productData
      });
      setUseSupabase(false);
    }
  };

  const updateProduct = async (id: string | number, productData: Partial<Product>): Promise<void> => {
    try {
      if (useSupabase) {
        const updatedProduct = await updateProductInSupabase(id, productData);
        dispatch({
          type: 'UPDATE_PRODUCT',
          payload: updatedProduct
        });
        window.dispatchEvent(new CustomEvent('productUpdated', { 
          detail: updatedProduct 
        }));
      } else {
        // Fallback to localStorage
        dispatch({
          type: 'UPDATE_PRODUCT',
          payload: { id, ...productData }
        });
        const updatedProduct = { id, ...productData };
        window.dispatchEvent(new CustomEvent('productUpdated', { 
          detail: updatedProduct 
        }));
      }
    } catch (error) {
      console.error('Error updating product:', error);
      showError('Failed to update product. Please try again.');
      // Fallback to localStorage
      dispatch({
        type: 'UPDATE_PRODUCT',
        payload: { id, ...productData }
      });
      setUseSupabase(false);
    }
  };

  const deleteProduct = async (id: string | number): Promise<void> => {
    try {
      if (useSupabase) {
        await deleteProductFromSupabase(id);
      }
      dispatch({
        type: 'DELETE_PRODUCT',
        payload: id
      });
      
      window.dispatchEvent(new CustomEvent('productDeleted', { 
        detail: { id } 
      }));
    } catch (error) {
      console.error('Error deleting product:', error);
      showError('Failed to delete product. Please try again.');
      // Still remove from local state
      dispatch({
        type: 'DELETE_PRODUCT',
        payload: id
      });
    }
  };

  const getProductById = (id: string | number): Product | undefined => {
    return state.products.find(product => String(product.id) === String(id));
  };

  const getProductsByCategory = (category: string): Product[] => {
    if (category === 'all') {
      return state.products;
    }
    return state.products.filter(product => product.category === category);
  };

  const searchProducts = (searchTerm: string): Product[] => {
    if (!searchTerm) {
      return state.products;
    }
    const term = searchTerm.toLowerCase();
    return state.products.filter(product =>
      product.name.toLowerCase().includes(term) ||
      (product.description && product.description.toLowerCase().includes(term)) ||
      product.category.toLowerCase().includes(term)
    );
  };

  const clearAllProducts = (): void => {
    dispatch({
      type: 'SET_PRODUCTS',
      payload: []
    });
  };

  const exportProducts = (): void => {
    const dataStr = JSON.stringify(state.products, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fusion-mart-products.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const importProducts = (productsData: Product[] | string): boolean => {
    try {
      const products = Array.isArray(productsData) ? productsData : JSON.parse(productsData);
      dispatch({
        type: 'SET_PRODUCTS',
        payload: products
      });
      return true;
    } catch (error) {
      console.error('Error importing products:', error);
      return false;
    }
  };

  const getProductsCount = (): number => state.products.length;

  const getAvailableProducts = (): Product[] => state.products.filter(product => product.available !== false);

  const getAllCategories = (): Category[] => {
    try {
      const stored = localStorage.getItem('fusion_mart_categories');
      return stored ? JSON.parse(stored) : categories;
    } catch (error) {
      console.error('Error loading categories from localStorage:', error);
      return categories;
    }
  };
  
  const getCategoryById = (categoryId: string): Category | undefined => {
    const allCategories = getAllCategories();
    return allCategories.find(category => category.id === categoryId);
  };
  
  const getSubcategoryById = (categoryId: string, subcategoryId: string): Subcategory | null => {
    const category = getCategoryById(categoryId);
    if (!category) return null;
    return category.subcategories.find(sub => sub.id === subcategoryId) || null;
  };
  
  const getProductsBySubcategory = (categoryId: string, subcategoryId: string): Product[] => {
    return state.products.filter(product => 
      product.category === categoryId && product.subcategory === subcategoryId
    );
  };

  const value: ProductContextType = {
    products: state.products,
    addProduct,
    updateProduct,
    deleteProduct,
    getProductById,
    getProductsByCategory,
    searchProducts,
    clearAllProducts,
    exportProducts,
    importProducts,
    getProductsCount,
    getAvailableProducts,
    getAllCategories,
    getCategoryById,
    getSubcategoryById,
    getProductsBySubcategory,
    loading
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = (): ProductContextType => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};
